import { DayOfWeek } from '@caresend/types';
import dayjs from 'dayjs';
import { convertCentsToDollar } from './currency.js';
import { isOfTypeDateWithTimeFormat } from './model.js';
import { literalSeparator, Separator, capitalize } from './string.js';
import { initTimeZoneDate } from '../initializers/date.js';
import { isInEnum } from '../typeValidation/enum.js';
import { isNullish } from '../typeValidation/nullish.js';

const utcTimezoneName = 'UTC';
/**
 * Date Formatting
 *
 * Avoid using dayjs dayjs format() directly, use these date formatting
 * functions instead.
 *
 * TODO: Refactor as needed to simplify.
 */

/** Conversion between date formats */

/**
 * Convert a time in seconds from css to milliseconds.
 *
 * Example: '0.3s' → 300
 */

const cssSecondsToMs = sassSeconds => parseFloat(sassSeconds) * 1000;
const getUTCTimezoneDate = timestamp => ({
  timestamp,
  timeZone: utcTimezoneName
});
/** Times */

const dayjsToTime = time => {
  const isAbridged = time.minute() === 0;
  const abridgedFormat = 'h a';
  const fullFormat = 'h:mm a';
  return isAbridged ? time.format(abridgedFormat) : time.format(fullFormat);
};

const hoursToMilliseconds = hours => hours * 60 * 60 * 1000;
const millisecondsToSeconds = milliseconds => Math.round(milliseconds / 1000);
/**
 * Returns formated time string from a millisecond time
 *
 * Example: 3660000 → '1 hour and 1 minute'
 */

const timeInMsToHoursAndMinutes = timeInMs => {
  // Calculate days, hours, minutes
  const days = Math.floor(timeInMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor(timeInMs % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
  const minutes = Math.floor(timeInMs % (1000 * 60 * 60) / (1000 * 60));
  const daysString = days ? `${days} day${Math.abs(days) > 1 ? 's' : ''}, ` : '';
  const hoursString = hours ? `${hours} hour${Math.abs(hours) > 1 ? 's' : ''} and ` : '';
  const minutesString = `${minutes} minute${Math.abs(minutes) !== 1 ? 's' : ''}`;
  return `${daysString}${hoursString}${minutesString}`;
};
const addHoursToTimestamp = (timestamp, hours) => timestamp + hoursToMilliseconds(hours);
const timestampToISODate = timestamp => dayjs(timestamp).toISOString() // '2019-01-25T02:00:00.000Z'
;
const getCurrentUnixTimestamp = () => Math.ceil(Date.now() / 1000);
const msTimestampToUnixTimestamp = timestamp => Math.ceil(timestamp / 1000);

const dayjsToTimeWithTZ = (time, displayTZ = false) => {
  const isAbridged = time.minute() === 0;
  const displaytz = displayTZ ? ' z' : '';
  const abridgedFormat = `h a${displaytz}`;
  const fullFormat = `h:mm a${displaytz}`;
  return isAbridged ? time.format(abridgedFormat) : time.format(fullFormat);
};
/**
 * (e.g.) '{12346789, 'America/Chicago'} => '12:00 CDT'
 */


const timeZoneDateToTime = timeZoneDate => {
  const time = dayjs(timeZoneDate.timestamp).tz(timeZoneDate.timeZone);
  const displayTZ = timeZoneDate.timeZone !== dayjs.tz.guess();
  return dayjsToTimeWithTZ(time, displayTZ);
};
const numberToTime = timeNumber => {
  const time = dayjs(timeNumber);
  return dayjsToTime(time);
};
/**
 * Examples:
 * - `1030` => [10,30]
 * - `900` => [9,00]
 */

const getLast2DigitsAndLeftSide = num => {
  const last2Digits = num % 100;
  const leftSide = Math.floor(num / 100);
  return [leftSide, last2Digits];
};
/**
 * Examples:
 * - `100` => 3600000
 */

const getTimeInMsFromMilitaryTime = militaryTime => {
  const [hours, minutes] = getLast2DigitsAndLeftSide(Number(militaryTime));
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  return date.getTime();
};
/**
 * Examples:
 * - 3600000 => `100`
 * - 32458734 => `900`
 */

const timeInMsToMilitaryHour = timeInMs => {
  if (!timeInMs) return '0';
  const totalMinutes = Math.floor(timeInMs / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}${minutes.toString().padStart(2, '0')}`;
};
/**
 * Examples:
 * - `400` => `4 pm`
 * - `1830` => `6:30 pm`
 */

const militaryHourToTime = timeNumber => {
  const stringNumber = String(timeNumber).padStart(4, '0');
  const reformattedTime = dayjs(stringNumber, 'hmm');
  return dayjsToTime(reformattedTime);
};
const timeInstructionDetails = (time, short = false) => {
  if (!time) return '';
  return `${short ? '' : 'between '}
    ${militaryHourToTime(time.startTime)}
    ${short ? 'to' : 'and'}
    ${militaryHourToTime(time.endTime)}`;
};
const getTimeInstructionDetailsString = (procedure, scheduledTimeZone, short) => {
  var _a, _b;

  const timeInfo = (_b = (_a = procedure === null || procedure === void 0 ? void 0 : procedure.dateInfo) === null || _a === void 0 ? void 0 : _a.map(info => {
    const timeZone = scheduledTimeZone ? ` ${scheduledTimeZone}` : '';
    return `${timeInstructionDetails(info.timeInstruction, short)}${timeZone}`;
  })) !== null && _b !== void 0 ? _b : [];
  return timeInfo === null || timeInfo === void 0 ? void 0 : timeInfo.join(', ');
};
const areThereDifferentDates = array => {
  let days = [];
  array.forEach(item => {
    var _a, _b;

    days.push(dayjs((_a = item.dateInstruction) === null || _a === void 0 ? void 0 : _a.timestamp).tz((_b = item.dateInstruction) === null || _b === void 0 ? void 0 : _b.timeZone).format('DD/MM/YYYY'));
  });
  days = days.filter((item, index) => days.indexOf(item) === index);
  return days.length > 1;
};
/** Helpers */

const yesterday = new Date(Date.now() - 86400000);
/**
 * Formats a fixed UTC date of type TimeZoneDate.
 */

const formatUTCTimeZoneDate = (date, format) => {
  if (!date || date.timeZone !== 'UTC') return 'Unknown date';
  return dayjs.utc(date.timestamp).format(`${format}`);
};
/**
 * getNextWeekDay(1) returns next Monday's timestamp
 */

const getNextWeekDay = dayINeed => {
  const currentUtcTimezoneDate = getUTCTimezoneDate(Date.now());
  const currentDayOfWeek = formatUTCTimeZoneDate(currentUtcTimezoneDate, 'd'); // if we haven't yet passed the day of the week that I need:

  if (Number(currentDayOfWeek) < dayINeed) {
    // then just give me this week's instance of that day
    return dayjs().weekday(dayINeed).valueOf();
  } else {
    // otherwise, give me *next week's* instance of that same day
    return dayjs().weekday(dayINeed).add(7, 'day').valueOf();
  }
};
/**
 * Compares a date with a timezone attached to another date based on the user’s
 * timezone to see if they match.
 */

const timeZoneDateMatchesLocalDate = (dateA, dateB) => {
  if (!dateA && !dateB) return false;
  const dateAFormatted = dayjs(dateA === null || dateA === void 0 ? void 0 : dateA.timestamp).tz(dateA === null || dateA === void 0 ? void 0 : dateA.timeZone).format('MM/DD/YYYY');
  const dateBFormatted = dayjs(dateB).format('MM/DD/YYYY');
  return dateAFormatted === dateBFormatted;
};
/** i.e. `America/Regina` => `CST` */

const getTimeZoneLabel = timeZone => dayjs().tz(timeZone).format('z');
/**
 * Given a timestamp and timezone, return a formatted date string exactly
 * matching the requested format. TODO: This was changed to not match how
 * it is written.
 *
 * Similar to `formatLocalDate`, but:
 * - Time zone and timestamp are required
 * - Returns the exact format requested
 *
 * Examples:
 * ```
 * (1609459200000, 'America/Los_Angeles', 'YYYY-MM-DD') => '2020-12-31'
 * (1609459200000, 'America/Los_Angeles', 'h:mm a z') => '4:00 pm PST' // if your are outside of PST timezone.
 * ```
 */

const formatDateWithTimeZone = (timeZone, timestamp, format = 'MM/DD/YYYY') => {
  const date = dayjs(timestamp).tz(timeZone).format(format);
  const timeZoneDisplayed = dayjs(timestamp).tz(timeZone).format('z'); // If the format requested does NOT contain a time, undisplay the timeZone

  if (!isOfTypeDateWithTimeFormat(format)) {
    return date.replace(timeZoneDisplayed, '');
  }

  return date;
};
/**
 * Formats a date that has a timezone attached. The date will be displayed
 * in the appropriate time zone. If the user’s timezone does not match, and
 * the format includes time information, the time zone will display alongside
 * the date.
 */

const formatTimeZoneDate = (date, format, add = {
  duration: 0,
  type: 'minutes'
}) => {
  if (!date) return 'Unknown date';
  let formatSuffix = '';

  if (dayjs.tz.guess() !== date.timeZone && isOfTypeDateWithTimeFormat(format) && !format.endsWith('z')) {
    formatSuffix = ' z';
  }

  const dateToReturn = dayjs.utc(date.timestamp).tz(date.timeZone).add(add.duration, add.type);
  const formattedDate = dateToReturn.format(`${format}${formatSuffix}`);
  const timeZoneDisplayed = dateToReturn.format('z'); // If the format requested does NOT contain a time, undisplay the timeZone

  if (!isOfTypeDateWithTimeFormat(format)) {
    return formattedDate.replace(timeZoneDisplayed, '');
  }

  return formattedDate;
};
/**
 * Formats a date that has a timezone attached. The date will be displayed
 * in the appropriate time zone. The timezone will not display unless passed in
 * with the date format.
 */

const formatTimeZoneDateSimple = (date, format = 'M/D/YYYY') => {
  if (!date) return 'Unknown date';
  return dayjs.utc(date.timestamp).tz(date.timeZone).format(format);
};
/**
* Returns a string explaining the date/s and time window/s that the procedure is booked for
* Example:
* - One date, one time window
*   - `Scheduled for 09/16/2022. Between 6am - 10am`
* - Multiple dates, multiple time windows
*   - `Scheduled for multiple dates`
* - One date, multiple time windows
*   - `Scheduled for 09/16/2022`
*/

const procedureDateInfo = dateInfo => {
  var _a, _b, _c, _d;

  if ((dateInfo === null || dateInfo === void 0 ? void 0 : dateInfo.length) === 0) return '';
  if (!(dateInfo === null || dateInfo === void 0 ? void 0 : dateInfo[0]) || !(dateInfo === null || dateInfo === void 0 ? void 0 : dateInfo[0].timeInstruction)) return '';
  const date = formatTimeZoneDate(dateInfo[0].dateInstruction, 'MM/DD/YYYY');

  if ((dateInfo === null || dateInfo === void 0 ? void 0 : dateInfo.length) === 1) {
    const start = militaryHourToTime((_b = (_a = dateInfo[0]) === null || _a === void 0 ? void 0 : _a.timeInstruction) === null || _b === void 0 ? void 0 : _b.startTime);
    const end = militaryHourToTime((_d = (_c = dateInfo[0]) === null || _c === void 0 ? void 0 : _c.timeInstruction) === null || _d === void 0 ? void 0 : _d.endTime);
    return `Scheduled for ${date}. Between ${start} and ${end}`;
  }

  if ((dateInfo === null || dateInfo === void 0 ? void 0 : dateInfo.length) > 1 && areThereDifferentDates(dateInfo)) {
    return 'Scheduled for multiple dates';
  }

  if ((dateInfo === null || dateInfo === void 0 ? void 0 : dateInfo.length) > 1 && !areThereDifferentDates(dateInfo)) {
    return `Scheduled for ${date}`;
  }

  return '';
};
/** Local Dates */

/**
 * Returns local user's timezone
 */

const getLocalTimeZone = () => dayjs.tz.guess();
/**
 * Formats a date, adjusted to the time zone of the user. All values are
 * optional and a time zone label will be appended if the user's time zone
 * does not match the time zone passed.
 *
 * Examples (User in `America/Regina`):
 * ```
 * (1609459200000, 'h:mm a') => '6:00 pm'
 * (1609459200000, 'h:mm a z') => '6:00 pm CST'
 * (1609459200000, 'YYYY-MM-DD h:mm a') => '2020-12-31 6:00 pm'
 * (1609459200000, 'h:mm a', 'America/Regina') => '6:00 pm'
 * (1609459200000, 'h:mm a z', 'America/Regina') => '6:00 pm CST'
 * (1609459200000, 'h:mm a', 'America/Chicago') => '6:00 pm CST'
 * (1609459200000, 'h:mm a', 'America/Los_Angeles') => '4:00 pm PST'
 * (1609459200000, 'h:mm a', 'America/Los_Angeles', true) => '4:00 pm'
 * (1609459200000, 'h:mm a z', 'America/Los_Angeles', true) => '4:00 pm PST'
 * ```
 *
 * Bad examples/edge cases (User in `America/Regina`):
 * ```
 * (1609459200000, 'h:mm a z', 'America/Chicago') => '6:00 pm CST CST'
 * (1609459200000, 'h:mm a z', 'America/Los_Angeles') => '4:00 pm PST PST'
 * (1609459200000, undefined, 'America/Los_Angeles') => '2020-12-31T16:00:00-08:00'
 * (1609459200000, undefined, undefined) => '2020-12-31T18:00:00-06:00'
 * (undefined, undefined, undefined) => 'Invalid date'
 * (1609459200000, 'h:mm a z', 'America/Chiccago') throws Error('Invalid time zone specified: America/Chiccago')
 * ```
 */

const formatLocalDate = (timestamp, format, timeZone, hideTimeZone) => {
  if (timestamp === undefined) return 'Invalid date';
  const localTimeZone = dayjs.tz.guess();
  const timeZoneFormat = !hideTimeZone && localTimeZone !== timeZone ? ' z' : '';

  if (timeZone) {
    if (!format) return dayjs(timestamp).tz(timeZone).format();
    return dayjs(timestamp).tz(timeZone).format(`${format}${timeZoneFormat}`);
  }

  return dayjs(timestamp).format(format);
};
/** Fixed Dates */

/**
 * Formats a date that should not be impacted by the timezone of the user.
 * The DatePicker used to enter these dates must have the `useStartOfUtcDate`
 * prop set.
 */

const formatFixedDate = (timestamp, format = 'MM/DD/YYYY') => {
  if (timestamp === undefined) return 'Invalid date';
  return dayjs.utc(timestamp).format(format);
};
/**
 * Formats a date of birth so that it will not be impacted by the timezone of
 * the user. The DatePicker used to enter these dates must have the
 * `useStartOfUtcDate` prop set.
 */

const formatDateOfBirth = (timestamp, format = 'MM/DD/YYYY') => formatFixedDate(timestamp, format);
const getStartOfUTCDate = (date, timeZone) => {
  if (date === null) return null;
  const dayjsObj = dayjs(date);
  const startOfUtcDate = new Date(dayjsObj.tz(timeZone).year(), dayjsObj.tz(timeZone).utc().month(), dayjsObj.tz(timeZone).utc().date());
  return dayjs(startOfUtcDate).valueOf();
};
const getStartOfLocalDate = (date, timeZoneInput) => {
  if (!date) return null;
  const timeZone = timeZoneInput !== null && timeZoneInput !== void 0 ? timeZoneInput : dayjs.tz.guess();
  const day = dayjs(date).tz(timeZone).startOf('date');
  return day.valueOf();
};
/**
 * This function updates an input date (if providing a number, will assume GMT)
 * to output a value local to the taget timezone of the same time.
 *
 * e.g input 8:00am PST, America/New_York => 8:00am EST
 */

const changeTimeZone = (input, timeZone) => {
  let date;

  if (typeof input === 'number') {
    date = {
      timestamp: input,
      timeZone: 'GMT'
    };
  } else {
    date = input;
  }

  let timestamp = date.timestamp;

  if (timestamp) {
    const startOfDayInputOffset = dayjs(date.timestamp).tz(date.timeZone).utcOffset();
    const startOfDayTargetOffset = dayjs(date.timestamp).tz(timeZone).utcOffset();
    const totalOffset = startOfDayInputOffset - startOfDayTargetOffset;
    timestamp = dayjs(date.timestamp).add(totalOffset, 'minutes').valueOf();
  }

  return {
    timestamp,
    timeZone
  };
};
/**
 * Determine age given by a particular date of birth value.
 */

const getAge = dob => {
  if (!dob) return '';
  const localDob = changeTimeZone(dob, dayjs.tz.guess());
  const yearsDiff = dayjs().diff(localDob.timestamp, 'years');
  return `${yearsDiff} years`;
};
/**
 * If the date is within one day of today, returns a plain english word.
 * Otherwise, returns date.
 *
 * Example output:
 * - 'March 15'
 * - 'Yesterday'
 * - 'Today'
 * - 'Tomorrow'
 */

const formatDateBooking = timestamp => {
  const date = dayjs(timestamp);

  if (dayjs(new Date()).diff(date, 'days') >= 1) {
    return `${dayjs(timestamp).format('MMMM D')}`;
  }

  return `${date.calendar().split(' ')[0]}`;
};
/**
 * Formats a requested or finalized date for a procedure. Waypoint must be
 * passed in order to get a finalized date.
 *
 * Example output:
 * - 'Date TBD - Time TBD'
 * - 'March 15 - Time TBD'
 * - 'March 15 - Morning'
 * - 'March 15 - 9:30 am'
 * - 'Date TBD, Time TBD'
 * - 'March 15, Time TBD'
 * - 'March 15, Morning'
 * - 'March 15, 9:30 am'
 */

const formatFinalizedProcedureDate = (waypoint, separator = Separator.HYPHEN) => {
  const {
    date
  } = waypoint !== null && waypoint !== void 0 ? waypoint : {};
  let dateString = 'Date TBD';
  let timeString = 'Time TBD';

  if (date === null || date === void 0 ? void 0 : date.timestamp) {
    dateString = dayjs(date.timestamp).tz(date.timeZone).format('MMMM D');
    timeString = dayjs(date.timestamp).tz(date.timeZone).format('h:mm a');
  }

  return `${dateString}${literalSeparator[separator]}${timeString}`;
};
/**
 * Formats requested dates for a procedure.
 *
 * Example output:
 * - 'Date TBD - Time TBD'
 * - 'March 15 - Time TBD'
 * - 'March 15 - Morning, March 16 - Afternoon'
 * - 'March 15 - 9:30 am, March 16 - 10:30 am'
 */

const formatRequestedProcedureDate = (procedure, separator = Separator.HYPHEN) => {
  const {
    dateInfo
  } = procedure;
  let dateFormated = '';
  dateInfo === null || dateInfo === void 0 ? void 0 : dateInfo.forEach((option, index) => {
    var _a, _b, _c, _d;

    let dateString = 'Date TBD ';
    let timeString = 'Time TBD';

    if ((_a = option.dateInstruction) === null || _a === void 0 ? void 0 : _a.timestamp) {
      dateString = dayjs((_b = option.dateInstruction) === null || _b === void 0 ? void 0 : _b.timestamp).tz((_c = option.dateInstruction) === null || _c === void 0 ? void 0 : _c.timeZone).format('MMMM D');
    }

    if ((_d = option === null || option === void 0 ? void 0 : option.timeInstruction) === null || _d === void 0 ? void 0 : _d.name) {
      timeString = capitalize(option.timeInstruction.name);
    }

    const comma = index !== 0 ? ', ' : '';
    dateFormated += `${comma}${dateString}${literalSeparator[separator]}${timeString}`;
  });
  return dateFormated;
};
/**
 * Formats a shift's date and time window with time zone displayed at the end
 *
 * Example output:
 * 'July 26 2022 | 8:00 am to 8:00 pm PDT'
 * '8:00 am to 8:00 pm PDT'
 */

const formatShiftDateTime = (shift, includeDate = true) => {
  if (isNullish(shift.startTime) || isNullish(shift.endTime)) return;
  const date = formatTimeZoneDateSimple(shift.startTime, 'MMMM D, YYYY');
  const startTime = formatTimeZoneDateSimple(shift.startTime, 'h:mm a');
  const endTime = formatTimeZoneDateSimple(shift.endTime, 'h:mm a');
  const timeZone = getTimeZoneLabel(shift.startTime.timeZone);
  return `${includeDate ? `${date} | ` : ''}${startTime} to ${endTime} ${timeZone}`;
};
/**
 * Example output:
 * - '<formatted date>'
 * - 'Today, <formatted date>'
 * - 'Invalid date'
 */

const waypointDate = (waypoint, dateFormat, todayLogic) => {
  var _a, _b;

  if (!((_a = waypoint === null || waypoint === void 0 ? void 0 : waypoint.date) === null || _a === void 0 ? void 0 : _a.timestamp) || !((_b = waypoint === null || waypoint === void 0 ? void 0 : waypoint.date) === null || _b === void 0 ? void 0 : _b.timeZone)) return 'Invalid date';
  const date = dayjs(waypoint.date.timestamp).tz(waypoint.date.timeZone);
  let today = '';

  if (todayLogic) {
    const isThatToday = date.isToday();
    today = isThatToday ? 'Today, ' : '';
  }

  const format = date.format(dateFormat);
  return `${today}${format}`;
};
/**
 * Formats a draftWaypoints's date option
 *
 * Example output:
 * 'August 16 - 10:20 pm'
 * 'July 26 - 8:00 am - $70'
 */

const formatDraftWaypointDate = (dateInfo, includePrice) => {
  const date = formatTimeZoneDateSimple(dateInfo.date, 'MMMM D, YYYY');
  const startTime = formatTimeZoneDateSimple(dateInfo.date, 'h:mm a');
  const price = convertCentsToDollar(dateInfo.price);
  return `${date} - ${startTime}${includePrice ? ` - $${price}` : ''}`;
};
/**
 * Converts time from 24 hour format to a more readable string
 * 800 -> '8 am'
 * 1200 -> '12 pm'
 * 1330 -> '1:30 pm'
 */

const convertNumberToTime = number => {
  const hours = Math.floor(number / 100);
  const minutes = number % 100;
  const formattedHours = hours > 12 ? hours - 12 : hours;
  const formattedMinutes = minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : '';
  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedTime = `${formattedHours}${formattedMinutes === ':00' ? '' : formattedMinutes} ${period}`;
  return formattedTime;
};
/**
 * Function to convert a time to milliseconds
 * timeToMilliseconds(1200) -> 43200000 // 12:00 PM
 * timeToMilliseconds(800)  -> 28800000 // 8:00 AM
 * timeToMilliseconds(930)  -> 34200000 // 9:30 AM
 */

const timeToMilliseconds = time => {
  // Convert the input time to string to handle inputs like 800 (8:00 AM) correctly
  let timeStr = time.toString(); // Ensure the string is at least 4 characters long by adding leading zeros if needed

  while (timeStr.length < 4) {
    timeStr = '0' + timeStr;
  } // Extract hours and minutes from the string


  const hours = parseInt(timeStr.slice(0, -2));
  const minutes = parseInt(timeStr.slice(-2)); // Calculate total milliseconds from hours and minutes

  const milliseconds = (hours * 3600 + minutes * 60) * 1000;
  return milliseconds;
};
const formatDayOfWeek = dayOfWeek => capitalize(dayOfWeek).slice(0, 3);
function getDayOfWeek(date) {
  const dayOfWeekString = formatTimeZoneDateSimple(date, 'dddd').toLowerCase();

  if (!isInEnum(dayOfWeekString, DayOfWeek)) {
    throw Error('Invalid date');
  }

  return dayOfWeekString;
}
/**
 * Given a TimeZoneDate and a time number (e.g. 1400 for 2pm), returns a
 * timestamp for that specific time on that date.
 *
 * Example with Oct 10 2026 23:46 PDT:
 *
 * ```
 * getSpecificTimeOnDate(
 *   { timestamp: 1791701211648, timeZone: 'America/Los_Angeles' },
 *   1400,
 * )
 * ⬇️
 * {
 *   timestamp: 1791702000000, // 10/10/2026 2:00 pm PDT
 *   timeZone: 'America/Los_Angeles',
 * }
 * ```
 */

const getSpecificTimeOnDate = (date, timeNumber) => {
  if (!date.timestamp) throw Error('Invalid date');
  const startOfDate = getStartOfLocalDate(date.timestamp, date.timeZone);
  if (!startOfDate) throw Error('Invalid date');
  const timeFromStartOfDayInMs = timeToMilliseconds(parseInt(timeNumber, 10));
  const timestamp = startOfDate + timeFromStartOfDayInMs;
  return initTimeZoneDate(date.timeZone, timestamp);
};
/**
 * Converts a `TimeZoneDate` to an ISO date string that includes the time zone.
 *
 * Example:
 * ```
 * timeZoneDateToISODate({ timestamp: 1791701211648, timeZone: 'America/Los_Angeles' })
 * ⬇️
 * '2026-10-10T23:46:51-07:00'
 * ```
 */

const timeZoneDateToISODateWithTimeZone = ({
  timestamp,
  timeZone
}) => {
  if (!timestamp) throw Error('Invalid date');
  const date = dayjs(timestamp).tz(timeZone); // Default dayjs format is ISO 8601

  return date.format();
};

export { addHoursToTimestamp, areThereDifferentDates, changeTimeZone, convertNumberToTime, cssSecondsToMs, formatDateBooking, formatDateOfBirth, formatDateWithTimeZone, formatDayOfWeek, formatDraftWaypointDate, formatFinalizedProcedureDate, formatFixedDate, formatLocalDate, formatRequestedProcedureDate, formatShiftDateTime, formatTimeZoneDate, formatTimeZoneDateSimple, formatUTCTimeZoneDate, getAge, getCurrentUnixTimestamp, getDayOfWeek, getLast2DigitsAndLeftSide, getLocalTimeZone, getNextWeekDay, getSpecificTimeOnDate, getStartOfLocalDate, getStartOfUTCDate, getTimeInMsFromMilitaryTime, getTimeInstructionDetailsString, getTimeZoneLabel, getUTCTimezoneDate, hoursToMilliseconds, militaryHourToTime, millisecondsToSeconds, msTimestampToUnixTimestamp, numberToTime, procedureDateInfo, timeInMsToHoursAndMinutes, timeInMsToMilitaryHour, timeInstructionDetails, timeToMilliseconds, timeZoneDateMatchesLocalDate, timeZoneDateToISODateWithTimeZone, timeZoneDateToTime, timestampToISODate, utcTimezoneName, waypointDate, yesterday };
